/**
 * DailyBarsCard — three small-multiple 30-day bar charts (one series each,
 * one hue, no legend): reports generated, signature links sent,
 * questionnaire links sent.
 */

import { Bar, BarChart, ResponsiveContainer, Tooltip as RechartsTooltip, XAxis, YAxis } from "recharts";
import { FileText, PenNib, ClipboardText } from "@phosphor-icons/react";
import { formatDayKey, TelemetryTooltip } from "../../analytics/telemetryChartUtils";
import useCountUp from "../../shared/hooks/useCountUp";
import { chartAnimationsEnabled } from "../clinicDetailConstants";
import { CardSkeleton } from "./Skeletons";

const METRICS = [
  { key: "reports", label: "דוחות שנוצרו", icon: FileText },
  { key: "signatureLinks", label: "טפסים לחתימה שנשלחו", icon: PenNib },
  { key: "questionnaireLinks", label: "שאלונים שנשלחו", icon: ClipboardText },
];

const PRIMARY = "#3cc4a1"; // --color-primary-dark

function MiniChart({ metric, data }) {
  const total = data.reduce((s, d) => s + (d[metric.key] || 0), 0);
  const shown = useCountUp(total);
  const Icon = metric.icon;
  const chartData = data.map((d) => ({ day: formatDayKey(d.dateKey), [metric.key]: d[metric.key] || 0 }));
  return (
    <div className="ncd2-mini">
      <div className="ncd2-mini-head">
        <span className="ncd2-card-icon">
          <Icon size={14} weight="bold" />
        </span>
        <span className="ncd2-mini-label">{metric.label}</span>
        <strong className="ncd2-mini-total">{shown.toLocaleString("he-IL")}</strong>
      </div>
      <div className="ncd2-mini-chart" dir="ltr">
        <ResponsiveContainer width="100%" height={88}>
          <BarChart data={chartData} margin={{ top: 4, right: 2, left: 2, bottom: 0 }} barCategoryGap={2}>
            <XAxis
              dataKey="day"
              tickLine={false}
              axisLine={false}
              interval={6}
              tick={{ fontSize: 10, fill: "var(--color-text-light)" }}
            />
            <YAxis hide allowDecimals={false} />
            <RechartsTooltip content={<TelemetryTooltip />} cursor={{ fill: "rgba(77,217,180,0.08)" }} />
            <Bar
              dataKey={metric.key}
              name={metric.label}
              fill={PRIMARY}
              radius={[3, 3, 0, 0]}
              maxBarSize={12}
              isAnimationActive={chartAnimationsEnabled()}
              animationDuration={500}
            />
          </BarChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
}

export default function DailyBarsCard({ daily30, isLoading }) {
  if (isLoading) return <CardSkeleton height={88} lines={1} />;
  const data = daily30 || [];
  return (
    <section className="ncd2-card ncd2-daily">
      <header className="ncd2-card-head">
        <div className="ncd2-card-titles">
          <span className="ncd2-card-eyebrow">30 ימים אחרונים · לפי יום</span>
          <h3 className="ncd2-card-title">פעילות יומית</h3>
        </div>
      </header>
      <div className="ncd2-mini-grid">
        {METRICS.map((m) => (
          <MiniChart key={m.key} metric={m} data={data} />
        ))}
      </div>
    </section>
  );
}
